import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { Observable } from 'rxjs';
import { FilterCriteria } from '@app/shared/components/filter-sidebar/filter-sidebar.component';

export interface ClientSelectSearchResponse<TItem> {
  items: TItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ClientSelectHydrationResponse<TItem> {
  items: TItem[];
  missingIds: Array<string | number>;
}

export interface ClientSelectRequestOptions {
  routeTemplate: string;
  clientId: string;
  subsidiaryId: string;
  sourceKey: string;
  filters?: FilterCriteria[];
  idField?: string;
  secondarySortField?: string;
  page?: number;
  pageSize?: number;
  search?: string;
  lat?: number | null;
  lng?: number | null;
}

@Injectable({
  providedIn: 'root',
})
export class ClientSelectService {
  private readonly apiUrl = environment.apiUrl;

  constructor(private readonly http: HttpClient) {}

  searchRecords<TItem>(options: ClientSelectRequestOptions): Observable<ClientSelectSearchResponse<TItem>> {
    const url = this.buildUrl(options.routeTemplate, options.clientId, options.subsidiaryId);
    let params = new HttpParams()
      .set('page', String(options.page ?? 1))
      .set('pageSize', String(options.pageSize ?? 20));

    if (options.sourceKey) {
      if (Array.isArray(options.sourceKey)) {
      params = params.set('sourceKey', JSON.stringify(options.sourceKey));
      } else if (options.sourceKey?.trim()) {
        params = params.set('sourceKey', options.sourceKey?.trim());
      }
    }

    if (options.idField?.trim()) {
      params = params.set('idField', options.idField.trim());
    }

    if (options.secondarySortField?.trim()) {
      params = params.set('secondarySortField', options.secondarySortField.trim());
    }

    if (options.search?.trim()) {
      params = params.set('search', options.search.trim());
    }

    if (options.filters?.length) {
      params = params.set('filters', JSON.stringify(options.filters));
    }

    if (typeof options.lat === 'number' && Number.isFinite(options.lat)) {
      params = params.set('lat', String(options.lat));
    }

    if (typeof options.lng === 'number' && Number.isFinite(options.lng)) {
      params = params.set('lng', String(options.lng));
    }

    return this.http.get<ClientSelectSearchResponse<TItem>>(url, { params });
  }

  hydrateRecords<TItem>(options: Omit<ClientSelectRequestOptions, 'page' | 'pageSize' | 'search'> & { ids: Array<string | number> }): Observable<ClientSelectHydrationResponse<TItem>> {
    const url = this.buildUrl(options.routeTemplate, options.clientId, options.subsidiaryId);
    let params = new HttpParams()
    .set('ids', options.ids.map((id) => String(id)).join(','));

    if (options.sourceKey) {
      if (Array.isArray(options.sourceKey)) {
      params = params.set('sourceKey', JSON.stringify(options.sourceKey));
      } else if (options.sourceKey?.trim()) {
        params = params.set('sourceKey', options.sourceKey?.trim());
      }
    }

    if (options.idField?.trim()) {
      params = params.set('idField', options.idField.trim());
    }

    if (options.secondarySortField?.trim()) {
      params = params.set('secondarySortField', options.secondarySortField.trim());
    }

    if (typeof options.lat === 'number' && Number.isFinite(options.lat)) {
      params = params.set('lat', String(options.lat));
    }

    if (typeof options.lng === 'number' && Number.isFinite(options.lng)) {
      params = params.set('lng', String(options.lng));
    }

    return this.http.get<ClientSelectHydrationResponse<TItem>>(url, { params });
  }

  buildUrl(routeTemplate: string, clientId: string, subsidiaryId: string): string {
    const normalizedTemplate = routeTemplate.startsWith('http')
      ? routeTemplate
      : `${this.apiUrl}${routeTemplate.startsWith('/') ? '' : '/'}${routeTemplate}`;

    return normalizedTemplate
      .replace('{clientId}', encodeURIComponent(clientId))
      .replace('{subsidiaryId}', encodeURIComponent(subsidiaryId));
  }
}